const DONT_SHOW_AGAIN_KEY = 'smart-offers-shipping-policy-dont-show-again';
const DEFAULT_EXPIRATION_MS = 3 * 24 * 60 * 60 * 1000; // 3 jours par défaut

interface DontShowAgainStorage {
  value: boolean;
  expiresAt: number;
}

/**
 * Type guard to validate that parsed data matches DontShowAgainStorage type.
 */
function isValidDontShowAgainStorage(
  value: unknown
): value is DontShowAgainStorage {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'expiresAt' in value &&
    typeof (value as DontShowAgainStorage).value === 'boolean' &&
    typeof (value as DontShowAgainStorage).expiresAt === 'number'
  );
}

export class ShippingPolicyDialogLocalManager {
  static getDontShowAgain(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const stored = localStorage.getItem(DONT_SHOW_AGAIN_KEY);
      if (!stored) {
        return false;
      }

      const parsed = JSON.parse(stored);

      // Support legacy format (boolean) for backward compatibility
      if (typeof parsed === 'boolean') {
        return parsed;
      }

      // Validate the parsed data matches DontShowAgainStorage type
      if (!isValidDontShowAgainStorage(parsed)) {
        console.warn(
          'Invalid dont_show_again format in localStorage, resetting to default'
        );
        // Clear invalid data
        this.clearDontShowAgain();
        return false;
      }

      // Check if the value has expired
      const now = Date.now();
      if (now > parsed.expiresAt) {
        console.log('dont_show_again value has expired, clearing');
        this.clearDontShowAgain();
        return false;
      }

      return parsed.value;
    } catch (error) {
      console.error('Error reading dont_show_again from localStorage:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem(DONT_SHOW_AGAIN_KEY);
      } catch (clearError) {
        console.error('Error clearing corrupted dont_show_again:', clearError);
      }
      return false;
    }
  }

  /**
   * Set the dont_show_again value with optional expiration duration.
   * @param dontShowAgain - The boolean value to store
   * @param expirationMs - Duration in milliseconds before expiration (default: 3 days)
   */
  static setDontShowAgain(
    dontShowAgain: boolean,
    expirationMs: number = DEFAULT_EXPIRATION_MS
  ): void {
    if (typeof window === 'undefined') return;

    try {
      const expiresAt = Date.now() + expirationMs;
      const storage: DontShowAgainStorage = {
        value: dontShowAgain,
        expiresAt,
      };
      localStorage.setItem(DONT_SHOW_AGAIN_KEY, JSON.stringify(storage));
    } catch (error) {
      console.error('Error writing dont_show_again to localStorage:', error);
    }
  }

  static clearDontShowAgain(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(DONT_SHOW_AGAIN_KEY);
    } catch (error) {
      console.error('Error clearing dont_show_again from localStorage:', error);
    }
  }
}
